const API_BASE = import.meta.env.VITE_API_BASE ?? '';

const TOKEN_KEY = 'downinfo_site_access_token';
const EXPIRY_KEY = 'downinfo_site_access_expiry';
const SITE_ACCESS_HEADER = 'X-Site-Access-Token';

declare global {
  interface Window {
    __downinfoSiteAuthFetchInstalled?: boolean;
  }
}

interface SiteAuthStatus {
  enabled: boolean;
  hint: string;
}

interface SiteAuthLoginResponse {
  token: string;
  expiresAt: string | null;
  enabled: boolean;
}

function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

export function clearSiteAccessToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRY_KEY);
}

export function getSiteAccessToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  const expiry = localStorage.getItem(EXPIRY_KEY);

  if (!token) return null;

  if (expiry && Date.now() > Date.parse(expiry)) {
    clearSiteAccessToken();
    return null;
  }

  return token;
}

export function hasSiteAccessToken(): boolean {
  return Boolean(getSiteAccessToken());
}

export async function getSiteAuthStatus(): Promise<SiteAuthStatus> {
  const res = await fetch(apiUrl('/api/site-auth/status'));

  if (res.status === 404) {
    return { enabled: false, hint: '' };
  }

  if (!res.ok) {
    throw new Error('无法读取访问状态');
  }

  return await res.json();
}

export async function loginSite(accessKey: string): Promise<void> {
  const res = await fetch(apiUrl('/api/site-auth/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessKey }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.detail || '访问口令错误');
  }

  const payload = data as SiteAuthLoginResponse;
  if (!payload.enabled) return;

  if (!payload.token || !payload.expiresAt) {
    throw new Error('访问凭证无效');
  }

  localStorage.setItem(TOKEN_KEY, payload.token);
  localStorage.setItem(EXPIRY_KEY, payload.expiresAt);
}

function isApiRequest(input: RequestInfo | URL): boolean {
  const rawUrl =
    input instanceof Request
      ? input.url
      : input instanceof URL
        ? input.toString()
        : String(input);

  try {
    const url = new URL(rawUrl, window.location.origin);
    return url.pathname.startsWith('/api/');
  } catch {
    return rawUrl.startsWith('/api/') || rawUrl.includes('/api/');
  }
}

export function installSiteAuthFetchInterceptor() {
  if (window.__downinfoSiteAuthFetchInstalled) return;
  window.__downinfoSiteAuthFetchInstalled = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const token = getSiteAccessToken();
    let nextInit = init;

    if (token && isApiRequest(input)) {
      const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
      if (!headers.has(SITE_ACCESS_HEADER)) {
        headers.set(SITE_ACCESS_HEADER, token);
      }
      nextInit = { ...init, headers };
    }

    const response = await nativeFetch(input, nextInit);

    if (response.status === 401 && response.headers.get('X-Site-Auth-Required') === '1') {
      clearSiteAccessToken();
      window.dispatchEvent(new Event('downinfo-site-auth-expired'));
    }

    return response;
  };
}
